import { supabase } from '@/integrations/supabase/client';
import { Category } from '@/types';
import { getCurrentProfileId, addProfileId } from './supabase-operations';
import { CATEGORIES } from '@/data/categories';
import { BUSINESS_CATEGORIES } from '@/data/businessCategories';

// Check if categories have been initialized for current profile
export const checkCategoriesInitialized = async (): Promise<boolean> => {
  const profileId = getCurrentProfileId();
  const { data: user } = await supabase.auth.getUser();
  
  if (!user.user?.id) return false;

  const { data, error } = await supabase
    .from('profile_categories_initialized')
    .select('id')
    .eq('user_id', user.user.id)
    .eq('profile_id', profileId || null)
    .maybeSingle();

  return !!data && !error;
};

// Mark categories as initialized with atomic lock
export const markCategoriesInitialized = async (): Promise<boolean> => {
  const profileId = getCurrentProfileId();
  const { data: user } = await supabase.auth.getUser();
  
  if (!user.user?.id) return false;

  const { error } = await supabase
    .from('profile_categories_initialized')
    .insert({
      user_id: user.user.id,
      profile_id: profileId || null
    });

  // Error 23505 = already exists = another process created it
  if (error?.code === '23505') return false;
  if (error) throw error;
  
  return true; // Lock acquired successfully
};

// Create default categories (NO ENCRYPTION - plaintext names)
export const createDefaultCategories = async (
  profileType: 'personal' | 'business' = 'personal'
): Promise<Category[]> => {
  const profileId = getCurrentProfileId();
  const { data: user } = await supabase.auth.getUser();
  
  if (!user.user?.id) throw new Error('User not authenticated');

  // Try to acquire lock
  const lockAcquired = await markCategoriesInitialized();
  if (!lockAcquired) {
    // Another process is already creating, just load
    console.log('[Categories] Lock not acquired, loading existing categories');
    return loadCategoriesFromSupabase();
  }

  const defaultCategories = profileType === 'business' ? BUSINESS_CATEGORIES : CATEGORIES;
  
  // Prepare data WITHOUT encryption - plaintext names
  const batchData = defaultCategories.map(category => ({
    id: crypto.randomUUID(),
    name: category.name, // PLAINTEXT
    type: category.type,
    color: category.color,
    icon: category.icon,
    is_default: true,
    user_id: user.user.id,
    profile_id: profileId || null
  }));

  console.log(`[Categories] Creating ${batchData.length} default categories...`);

  const { error } = await supabase
    .from('categories')
    .insert(batchData);

  if (error) {
    console.error('[Categories] Error creating defaults:', error);
    throw error;
  }

  console.log(`[Categories] Successfully created ${batchData.length} categories`);

  return defaultCategories.map((cat, i) => ({
    ...cat,
    id: batchData[i].id,
    isDefault: true
  }));
};

// Load categories (NO DECRYPTION - direct plaintext)
export const loadCategoriesFromSupabase = async (): Promise<Category[]> => {
  const profileId = getCurrentProfileId();
  
  let query = supabase
    .from('categories')
    .select('*')
    .order('name', { ascending: true });

  if (profileId) {
    query = query.eq('profile_id', profileId);
  } else {
    query = query.is('profile_id', null);
  }

  const { data, error } = await query;

  if (error) throw error;

  // Direct mapping - no decryption needed
  return (data || []).map(row => ({
    id: row.id,
    name: row.name, // PLAINTEXT directly
    type: row.type as 'receita' | 'despesa',
    color: row.color,
    icon: row.icon,
    isDefault: row.is_default
  }));
};

// Save category (NO ENCRYPTION - plaintext)
export const saveCategoryToSupabase = async (category: Category): Promise<void> => {
  const { data: user } = await supabase.auth.getUser();
  
  const categoryData = addProfileId({
    id: category.id,
    name: category.name, // PLAINTEXT
    type: category.type,
    color: category.color,
    icon: category.icon,
    is_default: category.isDefault || false,
    user_id: user.user?.id
  });

  const { error } = await supabase
    .from('categories')
    .upsert(categoryData);

  if (error) {
    // Friendly duplicate error
    if (error.code === '23505') {
      throw new Error('Já existe uma categoria com esse nome. Escolha outro nome.');
    }
    throw error;
  }
};

// Delete category
export const deleteCategoryFromSupabase = async (categoryId: string): Promise<void> => {
  const { error } = await supabase
    .from('categories')
    .delete()
    .eq('id', categoryId);

  if (error) throw error;
};
